import { useEffect, useState } from 'react';
import { FileText, CheckCircle, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface ReceiptRequest {
  id: number;
  requestNum: string;
  donationId: number;
  donorName: string;
  donorPhone: string;
  donorEmail: string | null;
  idCardLast4: string | null;
  mailingAddress: string | null;
  amount: number;
  status: string;
  receiptUrl: string | null;
  issuedAt: string | null;
  adminNotes: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  sent: 'bg-green-100 text-green-700',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待處理',
  processing: '處理中',
  sent: '已寄出',
};

export default function AdminReceiptRequests() {
  const [requests, setRequests] = useState<ReceiptRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    const res = await apiFetch<{ requests: ReceiptRequest[] }>('/api/admin/receipt-requests');
    setLoading(false);
    if (res.success && res.data) setRequests(res.data.requests);
  };

  const updateStatus = async (id: number, status: string) => {
    const res = await apiFetch(`/api/admin/receipt-requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    if (res.success) loadRequests();
  };

  const filtered = requests.filter(r =>
    (r.donorName + r.donorPhone + r.donorEmail + r.requestNum).toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-brand-600" />
          <h1 className="text-2xl font-bold">收據申請</h1>
        </div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="搜尋..." className="px-4 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 w-64" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
      ) : (
        <div className="space-y-3">
          {filtered.length === 0 && !loading && (
            <div className="text-center py-16 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>暫無收據申請</p>
            </div>
          )}
          {filtered.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-gray-400">{r.requestNum}</span>
                  <span className="font-semibold text-gray-900">{r.donorName}</span>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-gray-100'}`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {r.status !== 'sent' && (
                    <>
                      <select value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)} className="px-3 py-1 rounded-lg text-xs font-semibold border border-gray-200 outline-none cursor-pointer">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <button onClick={() => updateStatus(r.id, 'sent')} className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-100">
                        <CheckCircle className="w-3 h-3" /> 標記已寄
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-gray-400 text-xs">捐款金額</p>
                  <p className="font-medium">HK$ {(r.amount / 100).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">電話</p>
                  <p className="font-medium">{r.donorPhone}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">電郵</p>
                  <p className="font-medium">{r.donorEmail || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">身份證尾四碼</p>
                  <p className="font-medium">{r.idCardLast4 || '—'}</p>
                </div>
              </div>
              {r.mailingAddress && (
                <div className="mt-2 text-sm">
                  <p className="text-gray-400 text-xs">郵寄地址</p>
                  <p className="font-medium text-gray-700">{r.mailingAddress}</p>
                </div>
              )}
              {r.issuedAt && (
                <div className="mt-2 text-sm">
                  <p className="text-gray-400 text-xs">寄出時間</p>
                  <p className="font-medium text-gray-700">{new Date(r.issuedAt).toLocaleString('zh-HK')}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
